/**
 * Paramètres d’affichage TV selon le nombre de joueurs en match.
 * Ordre alphabétique → lecture rapide : cycle cible ~28 s au pic.
 */
export type LiveLayoutConfig = {
  pageSize: number;
  columns: number;
  /** Durée d’affichage d’une page (ms). */
  pageDwellMs: number;
  /** Variante visuelle des cartes. */
  density: "comfort" | "cozy" | "compact";
};

const TARGET_CYCLE_MS = 28_000;
const MIN_DWELL_MS = 3_000;
const MAX_DWELL_MS = 5_500;

/**
 * Layout de rotation (taille de page + durée) selon le volume total.
 */
export function computeLiveLayout(playerCount: number): LiveLayoutConfig {
  if (playerCount <= 0) {
    return {
      pageSize: 1,
      columns: 1,
      pageDwellMs: MAX_DWELL_MS,
      density: "comfort",
    };
  }

  if (playerCount <= 6) {
    return {
      pageSize: playerCount,
      columns: Math.min(3, playerCount),
      pageDwellMs: MAX_DWELL_MS,
      density: "comfort",
    };
  }
  if (playerCount <= 12) {
    return {
      pageSize: playerCount,
      columns: 4,
      pageDwellMs: MAX_DWELL_MS,
      density: "comfort",
    };
  }
  if (playerCount <= 20) {
    return {
      pageSize: playerCount,
      columns: 5,
      pageDwellMs: MAX_DWELL_MS,
      density: "cozy",
    };
  }
  if (playerCount <= 40) {
    const pageSize = 16;
    const pageCount = Math.ceil(playerCount / pageSize);
    return {
      pageSize,
      columns: 4,
      pageDwellMs: dwellForPages(pageCount),
      density: "cozy",
    };
  }
  if (playerCount <= 70) {
    const pageSize = 20;
    const pageCount = Math.ceil(playerCount / pageSize);
    return {
      pageSize,
      columns: 5,
      pageDwellMs: dwellForPages(pageCount),
      density: "compact",
    };
  }

  // Pic (~100+)
  const pageSize = 24;
  const pageCount = Math.ceil(playerCount / pageSize);
  return {
    pageSize,
    columns: 6,
    pageDwellMs: dwellForPages(pageCount),
    density: "compact",
  };
}

/**
 * Colonnes / densité pour la page affichée.
 * Ne réutilise PAS computeLiveLayout(n) : celui-ci raisonne en volume total
 * (ex. 24 joueurs → 4 colonnes), ce qui écrase une page pleine de 24 cartes.
 */
export function computeLivePageVisual(
  entriesOnPage: number
): Pick<LiveLayoutConfig, "columns" | "density"> {
  if (entriesOnPage <= 0) {
    return { columns: 1, density: "comfort" };
  }
  if (entriesOnPage <= 6) {
    return {
      columns: Math.min(3, entriesOnPage),
      density: "comfort",
    };
  }
  if (entriesOnPage <= 12) {
    return { columns: 4, density: "comfort" };
  }
  if (entriesOnPage <= 16) {
    return { columns: 4, density: "cozy" };
  }
  if (entriesOnPage <= 20) {
    return { columns: 5, density: "cozy" };
  }
  // Page pleine pic (21–24) : 6 colonnes × 4 rangées
  return { columns: 6, density: "compact" };
}

function dwellForPages(pageCount: number): number {
  if (pageCount <= 1) return MAX_DWELL_MS;
  return clamp(
    Math.round(TARGET_CYCLE_MS / pageCount),
    MIN_DWELL_MS,
    MAX_DWELL_MS
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
